import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '../services/firebase';
import api, { forgotPassword } from '../services/api';
import { User, UserRole } from '../types';
import { userStore } from '../services/userStore';
import { setAnalyticsUserProperties } from '../utils/firebaseAnalytics';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    birthDate?: string;
    acceptedTerms: boolean;
    storeName?: string; // Optional store name for store admin registration
  }) => Promise<void>;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      try {
        if (!firebaseUser) {
          // Don't clear userStore on null - it might be a temporary state during auth transition
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Sync userStore with auth state changes
        // Only update userStore if firebaseUser is not null (don't clear on null - might be temporary)
        userStore.setUser(firebaseUser);
        console.log('✅ userStore synced with auth.currentUser via onAuthStateChanged');

        console.log('🔐 Firebase user detected:', firebaseUser.email);
        try {
          const backendUser = await api.post('/auth/login', { firebaseUid: firebaseUser.uid });
          let userData;
          try {
            const profile = await api.get('/users/profile');
            userData = { ...backendUser.data.user, ...profile.data };
            setUser(userData);
            console.log('✅ User authenticated and profile loaded');
          } catch (profileError) {
            console.warn('⚠️ Failed to fetch profile, using basic user data:', profileError);
            userData = backendUser.data.user;
            setUser(userData);
          }
          
          // Set Firebase Analytics user properties (exclude SUPERADMIN)
          if (userData.role && userData.role !== UserRole.SUPERADMIN) {
            await setAnalyticsUserProperties(userData.id, userData.role);
          }
          
          setIsAuthenticated(true);
        } catch (apiError: any) {
          console.error('❌ Backend API error:', apiError);
          // If backend is unreachable, still allow Firebase auth to work
          // User can use the app but some features may be limited
          if (apiError.code === 'ECONNABORTED' || apiError.message?.includes('timeout')) {
            console.warn('⚠️ Backend unreachable, continuing with Firebase auth only');
            setError('Backend server unreachable. Some features may be limited.');
          } else {
            setError(apiError.message || 'Failed to connect to backend');
          }
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err: any) {
        console.error('❌ Error checking auth state:', err);
        setError(err.message || 'Failed to check authentication');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const register = useCallback(async (registrationData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    birthDate?: string;
    acceptedTerms: boolean;
    storeName?: string; // Optional store name for store admin registration
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        registrationData.email,
        registrationData.password
      );

      // Use userCredential.user directly - it's available immediately after createUserWithEmailAndPassword
      // Store it in userStore so API interceptor can access it
      // This is critical because auth.currentUser may not be set immediately
      
      // Protect userStore from being cleared during registration
      userStore.setRegistering(true);
      userStore.setUser(userCredential.user);
      
      // Force token refresh to ensure it's available for subsequent requests
      await userCredential.user.getIdToken(true);

      const backendUser = await api.post('/auth/register', {
        firebaseUid: userCredential.user.uid,
        email: registrationData.email.trim(),
        firstName: registrationData.firstName.trim(),
        lastName: registrationData.lastName.trim(),
        birthDate: registrationData.birthDate || undefined,
        acceptedTerms: registrationData.acceptedTerms,
        storeName: registrationData.storeName, // Optional store name
      });

      // userStore is already set above with userCredential.user
      // The main onAuthStateChanged listener (in useEffect) will keep it in sync
      // No need for a separate listener here that might clear it prematurely

      const registeredUserData = backendUser.data.user;
      setUser(registeredUserData);
      setIsAuthenticated(true);
      
      // Set Firebase Analytics user properties (exclude SUPERADMIN)
      if (registeredUserData.role && registeredUserData.role !== UserRole.SUPERADMIN) {
        await setAnalyticsUserProperties(registeredUserData.id, registeredUserData.role);
      }
      
      // Remove registration protection after successful registration
      userStore.setRegistering(false);
    } catch (err: any) {
      // Remove protection even on error
      userStore.setRegistering(false);
      
      // Provide user-friendly error messages
      let errorMessage = 'Registration failed';
      if (err.code) {
        console.error('Firebase registration error:', err.code, err.message);
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered. Please try logging in instead.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use at least 6 characters.';
            break;
          default:
            errorMessage = err.message || 'Registration failed. Please try again.';
        }
      } else {
        // Backend error: show generic "try again later" instead of raw 400/500.
        // Exception: "already exists" → tell user to log in.
        const raw = err.response?.data?.error || err.message || '';
        console.error('Registration backend error:', err.response?.status, raw);
        const lower = String(raw).toLowerCase();
        if (lower.includes('already exists') || lower.includes('already registered')) {
          errorMessage = 'This email is already registered. Please try logging in instead.';
        } else {
          errorMessage = 'Registration is temporarily unavailable. Please try again later.';
        }
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (credentials: { email: string; password: string }) => {
    setIsLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      // Store user in userStore immediately (available before auth.currentUser is set)
      userStore.setUser(userCredential.user);

      // Get token - use getIdToken(false) first to use cache, then force refresh if needed
      try {
        // Try cached token first (faster)
        let token = await userCredential.user.getIdToken(false);
        
        // If no cached token, force refresh
        if (!token) {
          token = await userCredential.user.getIdToken(true);
        }
        
        console.log('✅ Token obtained');
      } catch (tokenError) {
        console.warn('⚠️ Token error (continuing anyway):', tokenError);
      }

      const backendUser = await api.post('/auth/login', { firebaseUid: userCredential.user.uid });

      let userData;
      try {
        const profile = await api.get('/users/profile');
        userData = { ...backendUser.data.user, ...profile.data };
        setUser(userData);
      } catch (profileError) {
        console.warn('⚠️ Failed to fetch profile during login, using basic user data:', profileError);
        userData = backendUser.data.user;
        setUser(userData);
      }

      // Set Firebase Analytics user properties (exclude SUPERADMIN)
      if (userData.role && userData.role !== UserRole.SUPERADMIN) {
        await setAnalyticsUserProperties(userData.id, userData.role);
      }

      setIsAuthenticated(true);
      console.log('✅ Authentication state set to true');
    } catch (err: any) {
      // Provide user-friendly error messages
      let errorMessage = 'Login failed';
      if (err.code) {
        console.error('Firebase login error:', err.code, err.message);
        switch (err.code) {
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email. Please check your email or sign up.';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password. Please try again.';
            break;
          case 'auth/invalid-credential':
          case 'auth/invalid-email-password':
            errorMessage = 'The email or password you entered is incorrect. Please check your credentials and try again.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This account has been disabled. Please contact support.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed login attempts. Please try again later or reset your password.';
            break;
          default:
            errorMessage = err.message || 'Login failed. Please check your email and password and try again.';
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Clear userStore before signing out
      userStore.clearUser();
      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);
    } catch (err: any) {
      const errorMessage = err.message || 'Logout failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Call backend endpoint instead of Firebase directly
      // This allows us to send custom branded emails from our domain
      await forgotPassword(email);
    } catch (err: any) {
      // For security, backend always returns success
      // But we still handle errors gracefully on the client side
      const errorMessage = err.message || 'Password reset failed';
      setError(errorMessage);
      // Don't throw error - backend returns success even on failure for security
      // This prevents email enumeration attacks
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const profile = await api.get('/users/profile');
      setUser((prev) => {
        if (prev) {
          return { ...prev, ...profile.data };
        }
        return profile.data;
      });
    } catch (error: any) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    error,
    register,
    login,
    logout,
    resetPassword,
    refreshUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
